import { CommonModule } from '@angular/common';
import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, NgZone, OnDestroy, OnInit, Output, QueryList, ViewChildren } from '@angular/core';

export interface CaptureResult {
  success: boolean;
  imageDataUrl?: string;
  error?: string;
}

@Component({
  selector: 'app-speech',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './speech.component.html',
  styleUrl: './speech.component.scss'
})
export class SpeechComponent implements OnInit, AfterViewInit, OnDestroy {
  recording: boolean = false;
  processing: boolean = false;
  @Input() text?: string;
  @Output() search = new EventEmitter<string>();
  @Output() started = new EventEmitter<boolean>();
  baseWords: string[] = [];
  banWords: number[] = [];
  words: string[] = [];
  private finalTranscript: string = '';
  private lastProcessedFinalIndex: number = -1; // Índice del último resultado final procesado
  private lastFinalTranscript: string = ''; // Última transcripción final procesada completa
  private currentInterimWords: string[] = []; // Palabras interinas actuales
  recognition: any;
  private isRecognitionStarting = false;

  @Input() actionButton?: 'waiting' | 'listening' | 'loading';
  @ViewChildren('progressBar') progressBarElements!: QueryList<ElementRef<HTMLCanvasElement>>;

  // Mapa de conversión de palabras locales
  private wordConversionMap: { [key: string]: string } = {
    'hierba': 'yerba',
    'toistos': 'twistos',
    'Twister': 'twistos',
    'twister': 'twistos',
    // Agregar más conversiones aquí: 'palabra_detectada': 'palabra_correcta'
  };

  // Lista de palabras censuradas
  private censoredWords: string[] = [
    'ejemplo_censurado'
    // Agregar más palabras censuradas aquí
  ];

  private canvas?: HTMLCanvasElement;
  private ctx?: CanvasRenderingContext2D;
  private animationId?: number;
  private audioData: number[] = [];
  private maxAudioData = 50; // Reducir para barras más gruesas en 275px de ancho
  private audioContext?: AudioContext;
  private analyser?: AnalyserNode;
  private microphone?: MediaStreamAudioSourceNode;
  private dataArray?: Uint8Array;
  preparingRecording: boolean = false;
  showCapturePreview = false;
  captureResult?: CaptureResult;

  isMobile = false;

  constructor(
    private readonly ngZone: NgZone,
    private readonly cdr: ChangeDetectorRef
  ) {}

  private detectMobile(): void {
    // Detectar si es un dispositivo móvil
    const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
    const isMobileDevice = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent.toLowerCase());
    const isMobileScreen = window.innerWidth <= 768;
    
    this.isMobile = isMobileDevice || isMobileScreen;
  }

  ngAfterViewInit() {
    // Solo inicializar canvas si NO es móvil
    if (this.isMobile) return;
    
    // Inicializar el canvas si ya existe
    if (this.progressBarElements.length > 0) {
      const element = this.progressBarElements.get(0);
      if (element && element.nativeElement) {
        this.initializeCanvas(element.nativeElement);
      }
    }

    // Observar cambios en los elementos del canvas
    this.progressBarElements.changes.subscribe((elements: QueryList<ElementRef<HTMLCanvasElement>>) => {
      if (elements.length > 0) {
        const element = elements.get(0);
        if (element && element.nativeElement) {
          this.initializeCanvas(element.nativeElement);
        }
      }
    });
  }

  ngOnDestroy() {
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
    }
    this.stopAudioCapture();
  }

  private initializeCanvas(canvas: HTMLCanvasElement) {
    console.log('Initializing canvas');
    this.canvas = canvas;
    this.ctx = canvas.getContext('2d') || undefined;

    if (this.ctx && this.canvas) {
      // Configurar el canvas
      this.canvas.width = this.canvas.offsetWidth;
      this.canvas.height = this.canvas.offsetHeight;

      // Configurar el contexto
      this.ctx.strokeStyle = '#8922e5';
      this.ctx.lineWidth = 2;
      this.ctx.lineCap = 'round';
    }
  }

  private clearCanvas() {
    if (!this.ctx || !this.canvas) return;
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
  }

  private drawAudioVisualization() {
    if (!this.ctx || !this.canvas) return;

    // Limpiar el canvas
    this.clearCanvas();

    const centerY = this.canvas.height / 2;
    const barWidth = this.canvas.width / this.maxAudioData;

    // Dibujar las barras de audio moviéndose de derecha a izquierda
    this.audioData.forEach((amplitude, index) => {
      // Calcular la posición desde la derecha
      const x = this.canvas!.width - (this.audioData.length - index) * barWidth;

      // Solo dibujar si la barra está dentro del canvas
      if (x >= 0) {
        const calculatedHeight = (amplitude / 255) * (this.canvas!.height * 2);
        const height = Math.max(2, calculatedHeight); // Mínimo 2px de altura

        // Color fijo como solicitaste
        this.ctx!.fillStyle = `#f4f4f4`;

        // Hacer las barras más gruesas y con menos espacio entre ellas
        const barThickness = Math.max(2, barWidth - 1);
        this.ctx!.fillRect(x, centerY - height / 2, barThickness, height);
      }
    });

    // Continuar la animación
    this.animationId = requestAnimationFrame(() => this.drawAudioVisualization());
  }

  private addAudioSample(amplitude: number) {
    this.audioData.push(amplitude);

    // Mantener solo las últimas muestras
    if (this.audioData.length > this.maxAudioData) {
      this.audioData.shift();
    }
  }

  ngOnInit() {
    // Detectar móvil primero
    this.detectMobile();

    // Inicializar reconocimiento de voz (tanto desktop como móvil), pero sin audio/contexto extra en móvil
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = (window as any).SpeechRecognition ||
                               (window as any).webkitSpeechRecognition;
      if (!SpeechRecognition) {
        alert('Speech recognition not supported in this browser');
        return;
      }
      
      this.recognition = new SpeechRecognition();
      this.recognition.continuous = true;
      this.recognition.interimResults = true;
      this.recognition.lang = 'es-ES';
      this.recognition.onresult = (event: any) => {
        if (this.isMobile) {
          // Lógica robusta para móvil (Android): maneja transcripciones acumulativas
          this.handleMobileResult(event);
        } else {
          // Lógica simple para desktop/iOS: comportamiento original más directo
          this.handleDesktopResult(event);
        }
      }
      this.recognition.onend = () => {
        if (this.recording) {
          // Reanudar automáticamente para no cerrar en móviles por silencio
          this.restartRecognition();
        } else {
          this.setIdleState();
        }
        this.stopAudioVisualization();
      };
      this.recognition.onerror = (event: any) => {
        // En móviles, errores comunes (aborted/no-speech/network) no deben cerrar si seguimos grabando
        if (this.recording && ['aborted', 'no-speech', 'network'].includes(event?.error)) {
          this.restartRecognition();
        } else {
          this.setIdleState();
        }
        this.stopAudioVisualization();
      };
      this.recognition.onstart = () => {
        this.isRecognitionStarting = false;
        this.startAudioVisualization();
      };
    } else {
      console.log('Speech recognition not supported in this browser');
      alert('Speech recognition not supported in this browser');
    }

    // Marcar para detección de cambios
    this.cdr.detectChanges();
  }

  private startAudioVisualization() {
    // En móvil no usamos visualización ni Web Audio para evitar abortos en Android
    if (this.isMobile) return;

    this.audioData = [];

    // Intentar inicializar captura de audio real
    this.initializeAudioCapture().then(() => {
      // Si se inicializó correctamente, usar datos reales
      if (this.analyser) {
        const updateAudioData = () => {
          if (this.recording) {
            const amplitude = this.getRealAudioData();
            this.addAudioSample(amplitude);
            setTimeout(updateAudioData, 100); // Actualizar cada 100ms para más suavidad
          }
        };
        updateAudioData();
      } else {
        // Fallback a simulación
        this.startSimulatedAudio();
      }
    }).catch(() => {
      // Fallback a simulación si hay error
      this.startSimulatedAudio();
    });

    this.drawAudioVisualization();
  }

  private stopAudioVisualization() {
    if (this.isMobile) return;
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
      this.animationId = undefined;
    }
    this.audioData = [];
    this.stopAudioCapture();
  }

  private async initializeAudioCapture() {
    try {
      // Crear contexto de audio
      this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      this.analyser = this.audioContext.createAnalyser();
      this.analyser.fftSize = 256;

      const bufferLength = this.analyser.frequencyBinCount;
      this.dataArray = new Uint8Array(bufferLength);

      // Obtener acceso al micrófono
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      this.microphone = this.audioContext.createMediaStreamSource(stream);
      this.microphone.connect(this.analyser);

      console.log('Audio capture initialized');
    } catch (error) {
      console.error('Error initializing audio capture:', error);
      // Fallback a simulación si no se puede acceder al micrófono
      this.startSimulatedAudio();
    }
  }

  private startSimulatedAudio() {
    console.log('Using simulated audio data');
    const simulateAudio = () => {
      if (this.recording) {
        // Generar amplitud más variada para simular audio real
        const baseAmplitude = Math.random() * 100 + 50; // Entre 50 y 150
        const variation = Math.sin(Date.now() * 0.01) * 30; // Variación sinusoidal
        const amplitude = Math.max(20, Math.min(255, baseAmplitude + variation));
        this.addAudioSample(amplitude);
        setTimeout(simulateAudio, 100); // Actualizar cada 100ms para más suavidad
      }
    };
    simulateAudio();
  }

  private stopAudioCapture() {
    if (this.microphone) {
      this.microphone.disconnect();
      this.microphone = undefined;
    }
    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = undefined;
    }
    this.analyser = undefined;
    this.dataArray = undefined;
  }

  private getRealAudioData(): number {
    if (!this.analyser || !this.dataArray) return 0;

    // @ts-ignore - TypeScript strict mode issue with Uint8Array types
    this.analyser.getByteFrequencyData(this.dataArray);

    // Calcular el promedio de las frecuencias para obtener la amplitud
    const average = Array.from(this.dataArray).reduce((sum, value) => sum + value, 0) / this.dataArray.length;
    return average;
  }

  // Método para convertir palabras según el mapa de conversión
  private convertWord(word: string): string {
    return this.wordConversionMap[word.toLowerCase()] || word;
  }

  // Método para verificar si una palabra está censurada
  private isWordCensored(word: string): boolean {
    return this.censoredWords.includes(word.toLowerCase());
  }

  // Método para procesar y limpiar las palabras detectadas
  private processDetectedWords(words: string[]): string[] {
    return words
      .map(word => this.convertWord(word)) // Aplicar conversiones
      .filter(word => !this.isWordCensored(word)) // Filtrar palabras censuradas
      .filter(word => word.trim().length > 0); // Filtrar palabras vacías
  }

  /**
   * Maneja resultados en móvil (Android) con lógica robusta para transcripciones acumulativas
   */
  private handleMobileResult(event: any) {
    let newFinalWords: string[] = [];
    let interimWords: string[] = [];
    
    // Procesar todos los resultados desde resultIndex
    for (let i = event.resultIndex; i < event.results.length; i++) {
      const transcript = event.results[i][0].transcript.trim();
      
      if (event.results[i].isFinal) {
        // Los resultados finales contienen la transcripción completa acumulada
        // Solo procesar si este índice es mayor al último procesado
        if (i > this.lastProcessedFinalIndex) {
          // Normalizar transcripciones para comparación (minúsculas, sin espacios extra)
          
          const normalizedLast = this.lastFinalTranscript.toLowerCase().trim();
          
          // Si no hay transcripción previa, todas las palabras son nuevas
          if (!normalizedLast) {
            const allWords = transcript.split(/\s+/).filter((w: string) => w.length > 0);
            newFinalWords = allWords;
          } else {
            // Extraer solo las palabras nuevas comparando con la última transcripción procesada
            const lastWords = this.lastFinalTranscript.split(/\s+/).filter((w: string) => w.length > 0);
            const allWords = transcript.split(/\s+/).filter((w: string) => w.length > 0);
            
            // Encontrar el índice donde empiezan las palabras nuevas
            let startIndex = 0;
            const minLength = Math.min(lastWords.length, allWords.length);
            
            for (let j = 0; j < minLength; j++) {
              if (lastWords[j].toLowerCase() === allWords[j].toLowerCase()) {
                startIndex = j + 1;
              } else {
                break;
              }
            }
            
            // Extraer solo las palabras nuevas
            newFinalWords = allWords.slice(startIndex);
          }
          
          // Actualizar el índice del último resultado final procesado
          this.lastProcessedFinalIndex = i;
          // Actualizar transcripciones
          this.lastFinalTranscript = transcript;
          this.finalTranscript += (this.finalTranscript ? ' ' : '') + transcript;
        }
      } else {
        // Palabras interinas: extraer solo las nuevas comparando con la última transcripción final
        const allInterimWords = transcript.split(/\s+/).filter((w: string) => w.length > 0);
        
        // Comparar con la última transcripción final procesada para extraer solo lo nuevo
        if (!this.lastFinalTranscript) {
          interimWords = allInterimWords;
        } else {
          const lastWords = this.lastFinalTranscript.split(/\s+/).filter((w: string) => w.length > 0);
          let startIndex = 0;
          const minLength = Math.min(lastWords.length, allInterimWords.length);
          
          for (let j = 0; j < minLength; j++) {
            if (lastWords[j].toLowerCase() === allInterimWords[j].toLowerCase()) {
              startIndex = j + 1;
            } else {
              break;
            }
          }
          interimWords = allInterimWords.slice(startIndex);
        }
      }
    }

    // Agregar solo las palabras finales nuevas a baseWords (sin duplicar)
    if (newFinalWords.length > 0) {
      const processedNewWords = this.processDetectedWords(newFinalWords);
      this.baseWords = [...this.baseWords, ...processedNewWords];
    }

    // Actualizar palabras interinas actuales (solo las últimas)
    this.currentInterimWords = this.processDetectedWords(interimWords);
    
    // Actualizar palabras mostradas: finales (sin baneadas) + interinas actuales
    this.processWords();
  }

  /**
   * Maneja resultados en desktop/iOS con lógica simple y directa (comportamiento original)
   */
  private handleDesktopResult(event: any) {
    // En desktop, procesar todas las palabras de todos los resultados (comportamiento original)
    let allWords: string[] = [];
    for (let i = 0; i < event.results.length; i++) {
      const words = event.results[i][0].transcript.split(' ');
      allWords = allWords.concat(words);
    }
    
    // Procesar las palabras con conversiones y censura
    this.baseWords = this.processDetectedWords(allWords);
    // En desktop no usamos palabras interinas separadas, solo mostramos las finales procesadas
    this.currentInterimWords = [];
    this.processWords();
  }

  processWords() {
    this.ngZone.run(() => {
      // Mostrar palabras finales (sin baneadas) + palabras interinas actuales
      const finalWords = this.baseWords.filter((_, index) => !this.banWords.includes(index));
      this.words = [...finalWords, ...this.currentInterimWords];
      console.log(this.words);
    });
  }

  reset() {
    // banear los indices de todas las palabras
    this.banWords = this.baseWords.map((_, index) => index);
    this.processWords();
  }

  banWord(word: string, filteredIndex: number) {
    // Obtener todas las palabras no baneadas
//    const nonBannedWords = this.baseWords.filter((_, index) => !this.banWords.includes(index));

    // Encontrar la palabra en el array original
    let currentFilteredIndex = 0;
    for (let i = 0; i < this.baseWords.length; i++) {
      if (!this.banWords.includes(i)) {
        if (currentFilteredIndex === filteredIndex && this.baseWords[i] === word) {
          this.banWords.push(i);
          break;
        }
        currentFilteredIndex++;
      }
    }

    this.processWords();
  }

  startRecording() {
    if (!this.recognition) return;
    // Reset de estado para no arrastrar palabras previas
    this.finalTranscript = '';
    this.lastFinalTranscript = '';
    this.baseWords = [];
    this.banWords = [];
    this.words = [];
    this.currentInterimWords = [];
    this.lastProcessedFinalIndex = -1;
    this.isRecognitionStarting = false;
    this.recording = true;
    this.recognition.start();
    this.started.emit(true);
    // NO cambiar actionButton aquí cuando viene de un binding dinámico del padre
    // El padre controla el estado cuando usa [actionButton]="..."
    // this.actionButton = 'listening';
  }

  setIdleState() {
    this.clearCanvas();
    // NO cambiar actionButton aquí cuando viene de un binding dinámico del padre
    // Solo cambiar si no hay binding dinámico (cuando actionButton no viene del padre)
    // El padre controla el estado cuando usa [actionButton]="..."
    // this.actionButton = 'waiting';
    this.recording = false;
    this.words = [];
  }

  closeCapturePreview() {
    this.showCapturePreview = false;
    this.captureResult = undefined;
  }

  confirmCapture(result: CaptureResult) {
    console.log('Captura confirmada desde speaker:', result);
    this.closeCapturePreview();
  }

  makeSearch() {
    // Validar si hay texto para enviar
    const queryText = this.words.join(' ')
    if (!queryText) {
      // No hay texto detectado, cerrar la grabación sin emitir el evento
      this.recording = false;
      if (this.recognition) {
        this.recognition.stop();
      }
      this.started.emit(false);
      this.setIdleState();
      return;
    }

    this.processing = true;
    // NO cambiar actionButton aquí cuando viene de un binding dinámico del padre
    // El padre debe controlar el estado cuando usa [actionButton]="..."
    // this.actionButton = 'loading';
    this.search.emit(queryText);
    this.recording = false;
    if (this.recognition) {
      this.recognition.stop();
    }
    this.started.emit(false);
  }

  result(text?: string) {
    this.words = [];
    this.banWords = [];
    this.banWords = [];
    this.processing = false;
    if (text) {
      this.text = text;
    }
  }

  /**
   * Reinicia el reconocimiento evitando múltiples start simultáneos.
   */
  private restartRecognition() {
    if (!this.recognition || !this.recording) return;
    if (this.isRecognitionStarting) return;
    this.isRecognitionStarting = true;
    try {
      this.recognition.start();
    } catch (err) {
      // en algunos navegadores lanzar start rápidamente da error; lo ignoramos
    } finally {
      // pequeño delay para evitar ráfagas
      setTimeout(() => {
        this.isRecognitionStarting = false;
      }, 100);
    }
  }

}
